import React, { useState, useEffect } from 'react';
import {
  Modal,
  Card,
  Avatar,
  Progress,
  Button,
  Space,
  Tag,
  Spin,
  message,
  Typography,
  Row,
  Col,
  Descriptions,
  Tooltip
} from 'antd';
import {
  UserOutlined,
  EnvironmentOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  StarOutlined,
  ThunderboltOutlined,
  ToolOutlined
} from '@ant-design/icons';
import { interventionsService } from '../services/interventionsService';

const { Title, Text } = Typography;

interface TechnicianSuggestion {
  technician_id: number;
  name: string;
  email: string;
  phone: string;
  avatar: string | null;
  availability_status?: string;
  total_score: number;
  details: {
    distance_score: number;
    distance_km: number;
    skills_score: number;
    matched_skills: string[];
    availability_score: number;
    next_available: string;
    workload_score: number;
    recent_interventions: number;
    performance_score: number;
    avg_rating: number;
    total_ratings: number;
  };
}

interface TechnicianSuggestionsModalProps {
  visible: boolean;
  interventionId: number;
  interventionTitle: string;
  onClose: () => void;
  onAssigned?: () => void;
}

export const TechnicianSuggestionsModal: React.FC<TechnicianSuggestionsModalProps> = ({
  visible,
  interventionId,
  interventionTitle,
  onClose,
  onAssigned
}) => {
  const [loading, setLoading] = useState(false);
  const [assigning, setAssigning] = useState<number | null>(null);
  const [suggestions, setSuggestions] = useState<TechnicianSuggestion[]>([]);

  useEffect(() => {
    if (visible && interventionId) {
      loadSuggestions();
    }
  }, [visible, interventionId]);

  const loadSuggestions = async () => {
    setLoading(true);
    try {
      const response = await interventionsService.suggestTechnicians(interventionId, 10);
      if (response.success && response.data) {
        setSuggestions(response.data.suggestions || []);
      }
    } catch (error: any) {
      message.error(error.message || 'Erreur lors du chargement des suggestions');
    } finally {
      setLoading(false);
    }
  };

  const handleAssign = async (technicianId: number) => {
    setAssigning(technicianId);
    try {
      await interventionsService.assignTechnician(interventionId, technicianId);
      message.success('Technicien assigné avec succès');
      onAssigned?.();
      onClose();
    } catch (error: any) {
      message.error(error.message || 'Erreur lors de l\'assignation');
    } finally {
      setAssigning(null);
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 80) return '#52c41a';
    if (score >= 60) return '#faad14';
    if (score >= 40) return '#ff7a45';
    return '#ff4d4f';
  };

  return (
    <Modal
      title={
        <Space>
          <UserOutlined />
          <span>Suggestions de techniciens - {interventionTitle}</span>
        </Space>
      }
      open={visible}
      onCancel={onClose}
      width={900}
      footer={[
        <Button key="close" onClick={onClose}>
          Fermer
        </Button>
      ]}
      bodyStyle={{ maxHeight: '70vh', overflowY: 'auto' }}
    >
      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <Spin size="large" />
          <div style={{ marginTop: 16 }}>Analyse des techniciens disponibles...</div>
        </div>
      ) : suggestions.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <Text type="secondary">Aucun technicien disponible pour cette intervention</Text>
        </div>
      ) : (
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          {suggestions.map((suggestion, index) => (
            <Card
              key={suggestion.technician_id}
              style={{
                borderLeft: index === 0 ? '4px solid #52c41a' : undefined,
                backgroundColor: index === 0 ? '#f6ffed' : undefined
              }}
            >
              <Row gutter={16} align="middle">
                <Col>
                  <Avatar
                    size={64}
                    src={suggestion.avatar ? `${(process.env.REACT_APP_API_URL || 'http://localhost:3000/api').replace(/\/api$/, '')}${suggestion.avatar}` : undefined}
                    icon={<UserOutlined />}
                  />
                </Col>
                <Col flex="auto">
                  <Space direction="vertical" size="small" style={{ width: '100%' }}>
                    <Space wrap>
                      <Title level={5} style={{ margin: 0 }}>
                        {suggestion.name}
                      </Title>
                      {suggestion.availability_status === 'available' ? (
                        <Tag color="success">🟢 En ligne</Tag>
                      ) : suggestion.availability_status === 'busy' ? (
                        <Tag color="warning">🟠 En intervention</Tag>
                      ) : (
                        <Tag color="default">⚪ Hors ligne</Tag>
                      )}
                      {index === 0 && (
                        <Tag color="green" icon={<StarOutlined />}>
                          Meilleur choix
                        </Tag>
                      )}
                    </Space>
                    <Space split="|">
                      <Text type="secondary">{suggestion.email}</Text>
                      <Text type="secondary">{suggestion.phone}</Text>
                    </Space>
                  </Space>
                </Col>
                <Col>
                  <Space direction="vertical" align="center" size="small">
                    <div style={{ textAlign: 'center' }}>
                      <Progress
                        type="circle"
                        percent={suggestion.total_score}
                        strokeColor={getScoreColor(suggestion.total_score)}
                        width={80}
                      />
                      <div style={{ marginTop: 8, fontWeight: 'bold' }}>Score global</div>
                    </div>
                    <Button
                      type="primary"
                      size="small"
                      loading={assigning === suggestion.technician_id}
                      disabled={assigning !== null && assigning !== suggestion.technician_id}
                      onClick={() => handleAssign(suggestion.technician_id)}
                    >
                      Assigner
                    </Button>
                  </Space>
                </Col>
              </Row>

              <Descriptions
                column={2}
                size="small"
                style={{ marginTop: 16 }}
                bordered
              >
                <Descriptions.Item
                  label={
                    <Space>
                      <EnvironmentOutlined />
                      Distance
                    </Space>
                  }
                >
                  <Space>
                    <Progress
                      percent={suggestion.details.distance_score}
                      size="small"
                      strokeColor={getScoreColor(suggestion.details.distance_score)}
                      style={{ width: 100 }}
                    />
                    <Text strong>{Number(suggestion.details.distance_km || 0).toFixed(1)} km</Text>
                  </Space>
                </Descriptions.Item>

                <Descriptions.Item
                  label={
                    <Space>
                      <ToolOutlined />
                      Compétences
                    </Space>
                  }
                >
                  <Space direction="vertical" size="small">
                    <Progress
                      percent={suggestion.details.skills_score}
                      size="small"
                      strokeColor={getScoreColor(suggestion.details.skills_score)}
                      style={{ width: 100 }}
                    />
                    {suggestion.details.matched_skills.length > 0 ? (
                      <Space size={4} wrap>
                        {suggestion.details.matched_skills.map((skill) => (
                          <Tag key={skill} color="blue" style={{ fontSize: 11 }}>
                            {skill}
                          </Tag>
                        ))}
                      </Space>
                    ) : (
                      <Text type="secondary" style={{ fontSize: 12 }}>Aucune compétence spécifique</Text>
                    )}
                  </Space>
                </Descriptions.Item>

                <Descriptions.Item
                  label={
                    <Space>
                      <ClockCircleOutlined />
                      Disponibilité
                    </Space>
                  }
                >
                  <Space direction="vertical" size="small">
                    <Progress
                      percent={suggestion.details.availability_score}
                      size="small"
                      strokeColor={getScoreColor(suggestion.details.availability_score)}
                      style={{ width: 100 }}
                    />
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {suggestion.details.next_available}
                    </Text>
                  </Space>
                </Descriptions.Item>

                <Descriptions.Item
                  label={
                    <Space>
                      <CheckCircleOutlined />
                      Charge de travail
                    </Space>
                  }
                >
                  <Space direction="vertical" size="small">
                    <Progress
                      percent={suggestion.details.workload_score}
                      size="small"
                      strokeColor={getScoreColor(suggestion.details.workload_score)}
                      style={{ width: 100 }}
                    />
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {suggestion.details.recent_interventions} interventions récentes
                    </Text>
                  </Space>
                </Descriptions.Item>

                <Descriptions.Item
                  label={
                    <Space>
                      <StarOutlined />
                      Performance
                    </Space>
                  }
                  span={2}
                >
                  <Space>
                    <Progress
                      percent={suggestion.details.performance_score}
                      size="small"
                      strokeColor={getScoreColor(suggestion.details.performance_score)}
                      style={{ width: 100 }}
                    />
                    <Text type="secondary">
                      Note moyenne: {Number(suggestion.details.avg_rating || 0).toFixed(1)}/5
                      ({suggestion.details.total_ratings} avis)
                    </Text>
                  </Space>
                </Descriptions.Item>
              </Descriptions>
            </Card>
          ))}
        </Space>
      )}
    </Modal>
  );
};
